import React from 'react';
import {
  FaTimes,
  FaCalendarAlt,
  FaMapMarkerAlt,
  FaClock,
  FaUser,
  FaFileAlt,
} from 'react-icons/fa';
import { UPLOADS_BASE } from '@/services/api';
import './ViewEventModal.css';

export interface EventRecord {
  id: number;
  title: string;
  short_description: string;
  host: string;
  is_one_day: number;
  start_date: string;
  start_time: string;
  end_date: string | null;
  end_time: string | null;
  location: string;
  is_free: number;
  full_details: string | null;
  feature_image: string | null;
  agenda_file: string | null;
  duration: string | null;
  requirements: string | null;
  status: string;
  created_at: string;
  updated_at: string | null;
}

function getUploadUrl(path: string | null): string {
  if (!path) return '';
  return path.startsWith('http') ? path : UPLOADS_BASE + path.replace(/^\//, '');
}

const formatTime = (t: string | null) => (t ? t.slice(0, 5) : '');

interface ViewEventModalProps {
  event: EventRecord | null;
  isOpen: boolean;
  onClose: () => void;
}

export const ViewEventModal: React.FC<ViewEventModalProps> = ({ event, isOpen, onClose }) => {
  if (!isOpen) return null;
  if (!event) return null;

  return (
    <div
      className="kf-modal__overlay view-event-modal__overlay"
      onClick={(e) => e.target === e.currentTarget && onClose()}
      role="dialog"
      aria-modal="true"
      aria-labelledby="view-event-modal-title"
    >
      <div className="kf-modal view-event-modal" onClick={(e) => e.stopPropagation()}>
        <div className="kf-modal__header">
          <h2 id="view-event-modal-title">Event Details</h2>
          <button type="button" className="kf-modal__close" onClick={onClose} aria-label="Close">
            <FaTimes />
          </button>
        </div>
        <div className="view-event-modal__body">
          {event.feature_image && (
            <div className="view-event-modal__image">
              <img src={getUploadUrl(event.feature_image)} alt="" />
            </div>
          )}
          <h3 className="view-event-modal__title">{event.title}</h3>
          <p className="view-event-modal__short-desc">{event.short_description}</p>

          <div className="view-event-modal__meta">
            <div className="view-event-modal__meta-item">
              <FaUser />
              <span><strong>Host:</strong> {event.host}</span>
            </div>
            <div className="view-event-modal__meta-item">
              <FaCalendarAlt />
              <span>
                {new Date(event.start_date).toLocaleDateString()}
                {event.end_date && event.end_date !== event.start_date && (
                  <> – {new Date(event.end_date).toLocaleDateString()}</>
                )}
              </span>
            </div>
            <div className="view-event-modal__meta-item">
              <FaClock />
              <span>
                {formatTime(event.start_time)}
                {event.end_time && <> – {formatTime(event.end_time)}</>}
                {event.duration && ` (${event.duration})`}
              </span>
            </div>
            <div className="view-event-modal__meta-item">
              <FaMapMarkerAlt />
              <span>{event.location}</span>
            </div>
            <div className="view-event-modal__meta-item">
              <span><strong>Admission:</strong> {event.is_free ? 'Free' : 'Paid'}</span>
            </div>
            <div className="view-event-modal__meta-item">
              <span><strong>Status:</strong> {event.status ? String(event.status).charAt(0).toUpperCase() + String(event.status).slice(1) : '—'}</span>
            </div>
          </div>

          {event.full_details && (
            <div className="view-event-modal__section">
              <h4>Full Details</h4>
              <div
                className="view-event-modal__html"
                dangerouslySetInnerHTML={{ __html: event.full_details }}
              />
            </div>
          )}

          {event.requirements && (
            <div className="view-event-modal__section">
              <h4>Requirements</h4>
              <p>{event.requirements}</p>
            </div>
          )}

          {event.agenda_file && (
            <div className="view-event-modal__section">
              <h4>Event Agenda</h4>
              <a
                href={getUploadUrl(event.agenda_file)}
                target="_blank"
                rel="noopener noreferrer"
                className="view-event-modal__agenda-link"
              >
                <FaFileAlt /> Download agenda
              </a>
            </div>
          )}
        </div>
        <div className="kf-modal__actions">
          <button type="button" className="kf-modal__btn-primary" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};
